import type { MapColumn, MapEdge, MapNode, SourceGraph } from './graph'

// Pure geometry for the source map: where each box sits and the path of each
// edge. No React and no DOM, so it can be tested and rendered the same way
// without JS. Heights are fixed per column because every box in a column holds
// the same kind of text, sized for the longest case (a detail line that wraps to
// two lines; a job's status line wrapping under its cadence). If copy outgrows a
// box, raise the height here.

export const LAYOUT = {
  nodeWidth: 232,
  columnGap: 148,
  rowGap: 12,
  outputGap: 24,
  height: { source: 66, job: 82, section: 66 },
} as const

export type Box = { x: number; y: number; w: number; h: number }
export type PlacedNode = MapNode & { box: Box }
export type PlacedEdge = MapEdge & { d: string } // SVG path data
export type MapLayout = { width: number; height: number; nodes: PlacedNode[]; edges: PlacedEdge[] }

export const edgeKey = (edge: MapEdge): string => `${edge.from}>${edge.to}`

const COLUMNS: readonly MapColumn[] = ['source', 'job', 'section']

// Gap above a node: the first in a column has none, an output node (it leaves
// the dashboard) sits a little further from the rest.
const gapAbove = (node: MapNode, index: number): number =>
  index === 0 ? 0 : LAYOUT.rowGap + (node.output === true ? LAYOUT.outputGap : 0)

export function layoutSourceMap(graph: SourceGraph): MapLayout {
  const stacks = COLUMNS.map((column) => {
    const members = graph.nodes.filter((node) => node.column === column)
    const h = LAYOUT.height[column]
    const total = members.reduce((sum, node, i) => sum + gapAbove(node, i) + h, 0)
    return { column, members, h, total }
  })
  const height = Math.max(...stacks.map((stack) => stack.total))

  const nodes: PlacedNode[] = stacks.flatMap((stack, columnIndex) => {
    // Each column is centred on the tallest one, so the edges between columns
    // leave and arrive near the middle rather than all hanging from the top.
    let y = (height - stack.total) / 2
    return stack.members.map((node, i) => {
      y += gapAbove(node, i)
      const box: Box = {
        x: columnIndex * (LAYOUT.nodeWidth + LAYOUT.columnGap),
        y,
        w: LAYOUT.nodeWidth,
        h: stack.h,
      }
      y += stack.h
      return { ...node, box }
    })
  })

  const byId = new Map(nodes.map((node) => [node.id, node]))
  const half = LAYOUT.columnGap / 2
  const edges: PlacedEdge[] = graph.edges.map((edge) => {
    const from = byId.get(edge.from)
    const to = byId.get(edge.to)
    if (from === undefined || to === undefined) {
      throw new Error(`Source map edge ${edgeKey(edge)} has an unknown endpoint`)
    }
    const x1 = from.box.x + from.box.w
    const y1 = from.box.y + from.box.h / 2
    const x2 = to.box.x
    const y2 = to.box.y + to.box.h / 2
    return { ...edge, d: `M ${x1} ${y1} C ${x1 + half} ${y1}, ${x2 - half} ${y2}, ${x2} ${y2}` }
  })

  return { width: 3 * LAYOUT.nodeWidth + 2 * LAYOUT.columnGap, height, nodes, edges }
}
